import { useState } from "react";
import { Mail, ArrowRight, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { auth } from "../../services/auth";
import { usePublicBrand } from "../../context/BrandContext";
import { FField, ErrBox, BtnPrimary, Spinner } from "./AuthComponents";

/* ── Logo zone — logo only, no name text (matches Login/Register) ── */
function LogoZone({ publicBrand }) {
  const [imgErr, setImgErr] = useState(false);
  const src = publicBrand?.logoUrl || "/assets/project_logo.png";
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        marginBottom: 28,
        paddingBottom: 24,
        borderBottom: "1px solid #edf0f4",
      }}
    >
      {imgErr ? (
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 14,
            background: "#f0f2f5",
            border: "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: "1rem",
              color: "#111111",
              letterSpacing: "0.04em",
            }}
          >
            MDP
          </span>
        </div>
      ) : (
        <img
          src={src}
          alt="Logo"
          style={{ height: 64, maxWidth: 180, objectFit: "contain" }}
          onError={() => setImgErr(true)}
        />
      )}
    </div>
  );
}

export default function ForgotPasswordPage() {
  const publicBrand = usePublicBrand();
  const [email, setEmail] = useState("");
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e?.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      await auth.requestPasswordResetLink(email);
      setOk(true);
    } catch (e) {
      setErr(e?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f5f6f8",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          background: "#ffffff",
          border: "1px solid #e0e5ee",
          borderRadius: 18,
          padding: "40px 40px",
          boxShadow: "0 4px 32px rgba(0,0,0,0.07)",
        }}
      >
        {/* Logo — only the image, no name text */}
        <LogoZone publicBrand={publicBrand} />

        {!ok ? (
          <>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "rgba(0,0,0,0.06)",
                  border: "1px solid rgba(0,0,0,0.12)",
                  marginBottom: 16,
                }}
              >
                <Mail size={24} style={{ color: "#111111" }} />
              </div>
              <div
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "0.6rem",
                  letterSpacing: "0.04em",
                  color: "#9ca3af",
                  marginBottom: 6,
                }}
              >
                Password recovery
              </div>
              <h2
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: "2.2rem",
                  lineHeight: 1.1,
                  color: "#111827",
                  margin: "0 0 10px",
                }}
              >
                Forgot Your Password?
              </h2>
              <p
                style={{
                  fontFamily: "'DM Sans'",
                  fontSize: "0.875rem",
                  color: "#6b7280",
                  lineHeight: 1.65,
                  margin: 0,
                  fontWeight: 300,
                }}
              >
                Enter your registered email and we'll send a secure,
                time-limited reset link.
              </p>
            </div>

            <ErrBox msg={err} />

            <form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column", gap: 14 }}
            >
              <FField
                label="Email address"
                icon={Mail}
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="you@example.com"
                required
              />
              <BtnPrimary type="submit" disabled={loading || !email}>
                {loading ? (
                  <>
                    <Spinner /> Sending…
                  </>
                ) : (
                  <>
                    Send Reset Link <ArrowRight size={15} />
                  </>
                )}
              </BtnPrimary>
            </form>

            <div style={{ marginTop: 18, textAlign: "center" }}>
              <Link
                to="/login"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  fontFamily: "'DM Sans'",
                  fontSize: "0.8rem",
                  color: "#9ca3af",
                  textDecoration: "none",
                }}
              >
                <ArrowLeft size={13} /> Back to Sign In
              </Link>
            </div>
          </>
        ) : (
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.2)",
                marginBottom: 16,
              }}
            >
              <Mail size={24} style={{ color: "#16a34a" }} />
            </div>
            <div
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "0.6rem",
                letterSpacing: "0.04em",
                color: "#9ca3af",
                marginBottom: 6,
              }}
            >
              Check your inbox
            </div>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: "2.2rem",
                lineHeight: 1.1,
                color: "#111827",
                margin: "0 0 12px",
              }}
            >
              Link Sent!
            </h2>
            <p
              style={{
                fontFamily: "'DM Sans'",
                fontSize: "0.875rem",
                color: "#6b7280",
                lineHeight: 1.65,
                marginBottom: 24,
                fontWeight: 300,
              }}
            >
              A reset link was sent to{" "}
              <strong style={{ color: "#374151" }}>{email}</strong>.<br />
              It expires in{" "}
              <strong style={{ color: "#111827" }}>30 minutes</strong>. Check
              your spam folder if needed.
            </p>
            <Link
              to="/login"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#111111",
                color: "#ffffff",
                borderRadius: 10,
                padding: "13px 22px",
                fontFamily: "'DM Sans'",
                fontWeight: 600,
                fontSize: "0.9rem",
                textDecoration: "none",
                boxShadow: "none",
              }}
            >
              Return to Sign In
            </Link>
          </div>
        )}
      </div>

      {/* T&C + Privacy Policy — below the card */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          marginTop: 20,
          flexWrap: "wrap",
        }}
      >
        <Link
          to={`${process.env.REACT_APP_LANDING_URL}/terms`}
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.75rem",
            color: "#9ca3af",
            textDecoration: "none",
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
        >
          Terms &amp; Conditions
        </Link>
        <span style={{ color: "#d1d5db", fontSize: "0.75rem" }}>·</span>
        <Link
          to={`${process.env.REACT_APP_LANDING_URL}/privacy`}
          style={{
            fontFamily: "'DM Sans'",
            fontSize: "0.75rem",
            color: "#9ca3af",
            textDecoration: "none",
            transition: "color 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = "#6b7280")}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#9ca3af")}
        >
          Privacy Policy
        </Link>
      </div>
    </div>
  );
}
